import { CssBaseline, StyledEngineProvider, Theme, ThemeProvider } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { BrowserRouter, useLocation } from "react-router-dom";
import { useDataRequest } from "../../common/hooks/api";
import { getCompliantProgressActions } from "../../common/reducers/getCompliantProgressSlice";
import { theme as appTheme } from "../../common/root/appTheme";
import { Api } from "../../common/service/Api";
import { PageLoading } from "../components";
import {
  useOrganizationUsers,
  usePrimaryOrganization,
  usePrimaryOrganizationId,
  useSelf,
  useSession,
} from "../hooks";
import { GetCompliantContextProvider } from "./GetCompliantContext";
import { GetCompliantPageRouter } from "./GetCompliantPageRouter";
import { StayCompliantPageRouter } from "./StayCompliantPageRouter";
import { SetupPageRouter } from "./SetupPageRouter";
import { MailboxDisconnectedBanner } from "../components/organisms/callouts/MailboxDisconnectedBanner";
import { configureBeacon, destroyBeacon, initBeacon, setIdentity } from "../util/beacon";
import { Routes } from "./routes";
import { useSelfOrganizationUser } from "../hooks/useOrganization";
import { StepComparison } from "../surveys/steps/GetCompliantSteps";
import { GetCompliantPageWrapper } from "../components/layout/GetCompliantPageWrapper";
import {
  isHrCenterOnboardingComplete,
  useOnboardingProgress,
} from "../../common/hooks/useOnboardingProgress";
import {
  HR_CENTER_SETUP_BANNER_DISMISSED_COOKIE_NAME,
  HRCenterSetupBanner,
} from "../components/organisms/callouts/HRCenterSetupBanner";
import { useLocalStorage } from "../hooks/useLocalStorage";

declare module "@mui/styles/defaultTheme" {
  interface DefaultTheme extends Theme {}
}

const isRequestDetail = (pathname: string): boolean => {
  return (
    pathname.startsWith("/requests/") &&
    !["/requests/new", "/requests/active", "/requests/closed"].includes(pathname)
  );
};

const isPrivacyCenterPreviewPopup = (pathname: string): boolean => {
  return pathname === Routes.privacyCenter.previewPopup;
};

const isHrCenterPage = (pathname: string): boolean => {
  return pathname.startsWith(Routes.hrCenter.root);
};

export const LoggedInApp = () => {
  useSession();

  return (
    <BrowserRouter>
      <WrappedRouter />
    </BrowserRouter>
  );
};

const WrappedRouter = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [locationPathname, setLocationPathname] = useState("");
  const [userIdentified, setUserIdentified] = useState(false);
  const [self, selfRequest] = useSelf();
  const organizationId = usePrimaryOrganizationId();
  const [organization, organizationRequest] = usePrimaryOrganization(true);
  const selfOrgUser = useSelfOrganizationUser();
  const [orgUsers, ,] = useOrganizationUsers(organizationId);
  const [onboardingProgress, onboardingProgressRequest] = useOnboardingProgress(organizationId);

  // Allow individual pages to show a custom banner (so we know not to show any default banners)
  const [showingCustomBanner, setShowingCustomBanner] = useState(false);

  const { request: featuresRequest } = useDataRequest({
    queryKey: ["features", organizationId],
    api: () => Api.organization.getOrganizationFeatures(organizationId),
  });

  const [dismissedHrCenterSetupBanner] = useLocalStorage(
    HR_CENTER_SETUP_BANNER_DISMISSED_COOKIE_NAME,
    "false",
  );

  useEffect(() => {
    if (selfRequest.success && self && !userIdentified) {
      setIdentity({
        name: `${self.firstName} ${self.lastName}`,
        email: self.email,
        signature: self.beaconSignature,
      });
      setUserIdentified(true);
    } else if (selfRequest.error) {
      destroyBeacon();
      initBeacon();
      configureBeacon();
    }
  }, [
    selfRequest,
    self,
    self?.beaconSignature,
    self?.email,
    self?.firstName,
    self?.lastName,
    userIdentified,
  ]);

  // HACK: When a location changes from a request detail page then close the beacon
  useEffect(() => {
    if (isRequestDetail(locationPathname) && locationPathname !== location.pathname) {
      configureBeacon();
      (window as any).Beacon("navigate", "/");
      (window as any).Beacon("close");
    }

    if (location.pathname !== locationPathname) {
      setLocationPathname(location.pathname);
    }
  }, [location, locationPathname]);

  useEffect(() => {
    if (organizationRequest.success && organization) {
      configureBeacon();
      dispatch(
        getCompliantProgressActions.set({
          progress: organization.getCompliantProgress,
          hrProgress: organization.getCompliantHrProgress,
        }),
      );
    }
  }, [organizationRequest, organization]);

  if (
    !self ||
    !selfOrgUser ||
    !organization ||
    organizationRequest.running ||
    // devSurveyAnswerRequest.running ||
    featuresRequest.running ||
    onboardingProgressRequest.running
  ) {
    // Can't route the user if we don't have all the necessary information that comes from these requests yet.
    return <PageLoading />;
  }

  const isAdmin = self.globalRole === "ADMIN";
  const isHrUser = selfOrgUser.role === "HR";
  const isMailboxDisconnected = organization.mailbox.mailboxStatus == "NOT_CONNECTED";

  const orgHasHrUser = orgUsers.some((user) => user.role === "HR");

  const isHrCenterSetup = isHrCenterOnboardingComplete(
    onboardingProgress?.hrCenterOnboardingProgress,
  );

  // Handle the case where an HR user tries to start the HR onboarding before the Business Survey has been submitted
  if (
    isHrUser &&
    organization.phase === "GET_COMPLIANT" &&
    !StepComparison.isAfter(organization.getCompliantProgress, "BusinessSurvey.BusinessSurvey")
  ) {
    return (
      <GetCompliantPageWrapper showNavSteps={false} fullWidth={true} disablePadding={true}>
        <div className="get-compliant-complete--container mt-xl">
          <p>
            You cannot access the HR Onboarding Steps until the user who invited you completes the
            Business Survey Step.
          </p>
        </div>
      </GetCompliantPageWrapper>
    );
  }

  let showMailboxDisconnectedBanner =
    isMailboxDisconnected && !isPrivacyCenterPreviewPopup(locationPathname) && !isHrUser;
  let showHrCenterSetupBanner =
    !isHrCenterSetup &&
    !isHrUser &&
    isHrCenterPage(locationPathname) &&
    dismissedHrCenterSetupBanner !== "true";

  let router;
  switch (organization.phase) {
    case "SETUP":
      if (isAdmin) {
        router = (
          <GetCompliantContextProvider>
            <GetCompliantPageRouter phase={organization.phase} />
          </GetCompliantContextProvider>
        );
      } else {
        router = <SetupPageRouter />;
      }
      break;
    case "GET_COMPLIANT":
      router = (
        <GetCompliantContextProvider>
          <GetCompliantPageRouter phase={organization.phase} />
        </GetCompliantContextProvider>
      );
      break;
    case "STAY_COMPLIANT":
    default:
      router = (
        <>
          {/* If a page has a custom banner, then show it, prioritized above any default banners */}
          {/* If both banners can be shown, then show the HR center setup banner */}
          {!showingCustomBanner &&
            (showHrCenterSetupBanner ? (
              <HRCenterSetupBanner alreadyInvitedHrUser={orgHasHrUser} />
            ) : (
              showMailboxDisconnectedBanner && <MailboxDisconnectedBanner />
            ))}
          <StayCompliantPageRouter
            isHrUser={isHrUser}
            setShowingCustomBanner={setShowingCustomBanner}
          />
        </>
      );
      break;
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        {router}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
